import { profitPlusTimestampToMs } from './profitPlusTimestamp';

/** Parse billing.currentPeriodEnd from Firestore, REST JSON, or ISO strings. */
export function parseBillingPeriodEnd(raw: unknown): Date | null {
  const ms = profitPlusTimestampToMs(raw);
  return ms != null ? new Date(ms) : null;
}

/** True when period end is missing or before now (strict — used for access gates). */
export function isBillingPeriodEndPast(
  raw: unknown,
  now: Date = new Date()
): boolean {
  const end = parseBillingPeriodEnd(raw);
  if (!end) return true;
  return end.getTime() < now.getTime();
}

/** True when a trialing org's trial window has ended. */
export function isTrialPeriodExpired(
  billing: { subscriptionStatus?: string; currentPeriodEnd?: unknown } | undefined
): boolean {
  if (billing?.subscriptionStatus !== 'trialing') return false;
  const end = parseBillingPeriodEnd(billing.currentPeriodEnd);
  if (!end) return true;
  return end.getTime() < Date.now();
}

/** Stripe past_due grace period (days) — keep in sync with server billing_access_service. */
export const STRIPE_PAST_DUE_GRACE_DAYS = 3;

/** VES soft early-renew prompt window (days before period end). */
export const VES_EARLY_RENEW_DAYS = 7;

/** Whole calendar days from today to period-end date (local calendar). */
export function calendarDaysUntilPeriodEnd(
  periodEnd: Date,
  now: Date = new Date()
): number {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(periodEnd);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Soft prompt to renew VES while still in-period (before grace).
 * True when paymentMethod=ves, status=active, and period ends within N days
 * (including today) but has not yet passed.
 */
export function shouldPromptVesEarlyRenew(
  billing:
    | {
        paymentMethod?: string;
        subscriptionStatus?: string;
        currentPeriodEnd?: unknown;
      }
    | undefined
    | null,
  now: Date = new Date(),
  withinDays: number = VES_EARLY_RENEW_DAYS
): boolean {
  if (!billing) return false;
  if (billing.paymentMethod !== 'ves') return false;
  if (billing.subscriptionStatus !== 'active') return false;
  const periodEnd = parseBillingPeriodEnd(billing.currentPeriodEnd);
  if (!periodEnd) return false;
  if (now.getTime() > periodEnd.getTime()) return false;
  const daysLeft = calendarDaysUntilPeriodEnd(periodEnd, now);
  return daysLeft >= 0 && daysLeft <= withinDays;
}

/** True when past_due grace has expired and the user should be sent to paywall. */
export function isStripePastDueExpired(
  billing: { subscriptionStatus?: string; currentPeriodEnd?: unknown } | undefined
): boolean {
  if (billing?.subscriptionStatus !== 'past_due') return false;
  const end = parseBillingPeriodEnd(billing.currentPeriodEnd);
  if (!end) return false;
  const daysSince = Math.floor(
    (Date.now() - end.getTime()) / (1000 * 60 * 60 * 24)
  );
  return daysSince > STRIPE_PAST_DUE_GRACE_DAYS;
}
